import { useCallback } from 'react'
import { Upload, type UploadProps, Spin, Typography } from 'antd'
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable'
import { useTranslation } from 'react-i18next'

import { SortableImageItem } from './SortableImageItem'

const MAX_IMAGES = 10
const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif'

interface ProductImageUploadProps {
  value?: string[]
  onChange?: (urls: string[]) => void
  onUpload: (file: File) => Promise<string>
  isUploading: boolean
}

export function ProductImageUpload({
  value = [],
  onChange,
  onUpload,
  isUploading,
}: ProductImageUploadProps) {
  const { t } = useTranslation()
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleCustomRequest: UploadProps['customRequest'] = (options) => {
    const file = options.file
    if (!(file instanceof File)) return
    onUpload(file)
      .then((url) => {
        const updated = [...value, url]
        onChange?.(updated)
        options.onSuccess?.(url)
      })
      .catch((error_: unknown) => {
        options.onError?.(
          error_ instanceof Error ? error_ : new Error(t('pages.products.messages.uploadFailed')),
        )
      })
  }

  const handleRemove = useCallback(
    (url: string) => {
      onChange?.(value.filter((v) => v !== url))
    },
    [value, onChange],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = value.indexOf(String(active.id))
      const newIndex = value.indexOf(String(over.id))
      if (oldIndex === -1 || newIndex === -1) return
      onChange?.(arrayMove(value, oldIndex, newIndex))
    },
    [value, onChange],
  )

  const canUploadMore = value.length < MAX_IMAGES

  return (
    <div>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        {t('pages.products.imageUpload.imageCount', { current: value.length, max: MAX_IMAGES })}
        {value.length > 1 && ` · ${t('pages.products.imageUpload.dragToReorder')}`}
      </Typography.Text>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={value} strategy={rectSortingStrategy}>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {value.map((url) => (
              <SortableImageItem key={url} id={url} url={url} onRemove={handleRemove} />
            ))}

            {canUploadMore && (
              <Spin spinning={isUploading} indicator={<LoadingOutlined />}>
                <Upload
                  data-testid="product-form-image-upload"
                  accept={ACCEPTED_IMAGE_TYPES}
                  listType="picture-card"
                  showUploadList={false}
                  customRequest={handleCustomRequest}
                  disabled={isUploading}
                >
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>
                      {t('pages.products.imageUpload.uploadButton')}
                    </div>
                  </div>
                </Upload>
              </Spin>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}
